import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Share2, Copy, Trash2, Globe, CheckCircle2, RefreshCw, ChevronDown, ChevronRight, Eye, UserCog } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function ClientPortalDialog({ clientId }) {
  const { activeWorkspace, activeWorkspaceId, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [portal, setPortal] = useState(null);
  const [showDomain, setShowDomain] = useState(false);
  const [domain, setDomain] = useState(activeWorkspace?.custom_domain || "");
  const [verifyToken, setVerifyToken] = useState(activeWorkspace?.custom_domain_verify_token || null);
  const [verifiedAt, setVerifiedAt] = useState(activeWorkspace?.custom_domain_verified_at || null);
  const [savingDomain, setSavingDomain] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    setDomain(activeWorkspace?.custom_domain || "");
    setVerifyToken(activeWorkspace?.custom_domain_verify_token || null);
    setVerifiedAt(activeWorkspace?.custom_domain_verified_at || null);
  }, [activeWorkspace?.id, activeWorkspace?.custom_domain]);

  const ensure = async () => {
    setOpen(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/portal`);
      setPortal(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not create portal link")); }
  };
  const baseOrigin = (verifiedAt && domain) ? `https://${domain}` : window.location.origin;
  const portalUrl = portal ? `${baseOrigin}/portal/${portal.token}` : "";
  const copyUrl = () => {
    if (!portal) return;
    navigator.clipboard.writeText(portalUrl);
    toast.success("Portal URL copied");
  };
  const revoke = async () => {
    try { await api.delete(`/clients/${clientId}/portal`); setPortal(null); setOpen(false); toast.success("Portal link revoked"); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not revoke")); }
  };

  const saveDomain = async () => {
    setSavingDomain(true);
    try {
      const { data } = await api.patch(`/workspaces/${activeWorkspaceId}/custom-domain`, { custom_domain: domain });
      setVerifyToken(data.verify_token || null);
      setVerifiedAt(data.verified_at || null);
      toast.success(domain ? "Domain saved — add the DNS records to verify" : "Domain cleared");
      refresh && (await refresh());
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSavingDomain(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/custom-domain/verify`);
      setVerifiedAt(data.verified_at);
      toast.success("Domain verified");
      refresh && (await refresh());
    } catch (e) { toast.error(apiErrorMessage(e, "Verification failed")); }
    finally { setVerifying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" onClick={ensure} className="rounded-none" data-testid="client-portal-btn">
          <Share2 className="w-4 h-4 mr-2" /> Client portal
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Client portal link</DialogTitle>
          <DialogDescription>One read-only URL your client can bookmark for every project & review item in this engagement.</DialogDescription>
        </DialogHeader>

        {portal ? (
          <div className="space-y-3" data-testid="portal-dialog-active">
            <div className="border border-border bg-muted/40 p-3 font-mono text-xs break-all" data-testid="portal-url">
              {portalUrl}
            </div>
            <div className="flex gap-2">
              <Button onClick={copyUrl} className="rounded-none flex-1" data-testid="portal-copy-btn">
                <Copy className="w-4 h-4 mr-2" /> Copy URL
              </Button>
              <Button onClick={revoke} variant="destructive" className="rounded-none" data-testid="portal-revoke-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Revoke
              </Button>
            </div>
            <ImpersonateBlock clientId={clientId} portalToken={portal.token} baseOrigin={baseOrigin} />
          </div>
        ) : (
          <div className="text-sm text-muted-foreground text-center py-6">Generating portal link…</div>
        )}

        <div className="border-t border-border pt-3 mt-3">
          <button
            type="button"
            onClick={() => setShowDomain((v) => !v)}
            className="w-full flex items-center justify-between text-sm font-medium"
            data-testid="portal-domain-toggle"
          >
            <span className="inline-flex items-center gap-2"><Globe className="w-4 h-4" /> Custom domain (workspace-wide)</span>
            {showDomain ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>

          {showDomain && (
            <div className="mt-3 space-y-3" data-testid="portal-domain-section">
              <p className="text-xs text-muted-foreground">
                Replace <code>{window.location.host}</code> in every portal URL with your own subdomain, e.g.{" "}
                <code>portal.acmecorp.com</code>. Applies to all client portal links in this workspace.
              </p>
              <div className="flex gap-2">
                <Input
                  value={domain}
                  onChange={(e) => setDomain(e.target.value.trim().toLowerCase())}
                  placeholder="portal.yourdomain.com"
                  className="h-9 text-xs"
                  data-testid="portal-domain-input"
                />
                <Button onClick={saveDomain} disabled={savingDomain || domain === (activeWorkspace?.custom_domain || "")} className="rounded-none h-9" data-testid="portal-domain-save">
                  {savingDomain ? "Saving…" : "Save"}
                </Button>
              </div>

              {domain && verifyToken && !verifiedAt && (
                <div className="border border-amber-500/30 bg-amber-500/5 p-3 space-y-2 text-xs" data-testid="portal-domain-verify-block">
                  <div className="font-semibold text-amber-700 dark:text-amber-400">Add these DNS records, then click Verify:</div>
                  <DnsRow label="CNAME" name={domain} value={(window.location.host || "app.creativeops.io")} />
                  <DnsRow label="TXT" name={`_creativeops-verify.${domain}`} value={verifyToken} />
                  <Button onClick={verify} disabled={verifying} variant="outline" size="sm" className="rounded-none h-7 text-xs" data-testid="portal-domain-verify-btn">
                    <RefreshCw className={`w-3 h-3 mr-1 ${verifying ? "animate-spin" : ""}`} /> {verifying ? "Verifying…" : "Verify now"}
                  </Button>
                </div>
              )}

              {verifiedAt && (
                <div className="border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs inline-flex items-center gap-2" data-testid="portal-domain-verified">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Verified · all portal links will use <code>{domain}</code>.
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DnsRow({ label, name, value }) {
  const copy = () => { navigator.clipboard.writeText(value); toast.success("Copied"); };
  return (
    <div className="grid grid-cols-12 gap-2 items-center">
      <div className="col-span-2 font-mono uppercase text-[10px] text-muted-foreground">{label}</div>
      <div className="col-span-5 font-mono text-[10px] truncate">{name}</div>
      <div className="col-span-4 font-mono text-[10px] truncate">{value}</div>
      <button type="button" onClick={copy} className="col-span-1 text-muted-foreground hover:text-foreground" title="Copy"><Copy className="w-3 h-3 ml-auto" /></button>
    </div>
  );
}

function ImpersonateBlock({ clientId, portalToken, baseOrigin }) {
  const { activeWorkspaceId } = useAuth();
  const [busy, setBusy] = useState(false);

  const impersonate = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/clients/${clientId}/impersonate-portal`,
        { mode },
      );
      if (mode === "view") {
        const url = portalToken ? `${baseOrigin}/portal/${portalToken}` : null;
        if (url) window.open(url, "_blank", "noopener");
        toast.success("Opened portal — this action is logged in the audit trail.");
      } else if (mode === "act") {
        // Store impersonation JWT and open the client portal in a new tab as a
        // logged-in portal user. The Portal app reads `cp_token` from storage.
        try {
          sessionStorage.setItem("cp_impersonation_token", data.jwt);
          sessionStorage.setItem("cp_impersonation_client", clientId);
        } catch { /* noop */ }
        const url = portalToken ? `${baseOrigin}/portal/${portalToken}?impersonating=1` : null;
        if (url) window.open(url, "_blank", "noopener");
        toast.success("Acting as client — every action will be attributed to you in the audit trail.");
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Impersonation failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-border p-3 space-y-2" data-testid="portal-impersonate-block">
      <div className="label-eyebrow text-[10px] flex items-center gap-1.5"><UserCog className="w-3 h-3" /> Impersonate portal</div>
      <p className="text-[11px] text-muted-foreground">
        Open the client's portal to preview what they see, or act as them for troubleshooting. Every impersonation session is written to the audit log.
      </p>
      <div className="flex gap-2">
        <Button
          onClick={() => impersonate("view")}
          disabled={busy || !portalToken}
          variant="outline"
          className="rounded-none flex-1 h-8 text-xs"
          data-testid="portal-impersonate-view-btn"
        >
          <Eye className="w-3.5 h-3.5 mr-1.5" /> View as client
        </Button>
        <Button
          onClick={() => impersonate("act")}
          disabled={busy || !portalToken}
          variant="outline"
          className="rounded-none flex-1 h-8 text-xs border-amber-500/50 text-amber-700 hover:bg-amber-500/10"
          data-testid="portal-impersonate-act-btn"
        >
          <UserCog className="w-3.5 h-3.5 mr-1.5" /> Act as client
        </Button>
      </div>
    </div>
  );
}
